import React, { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import EntityMultiSelect from '../../multi_select/EntityMultiSelect';
import Summary from '../components/Summary';
import SampleTable from '../components/Tables/SampleTable';
import BonusesModal from '../components/Modals/BonusesModal';
import StatementsModal from '../components/Modals/StatementsModal';
import WeeklyDatePicker from '../../payments/paymentsComponents/WeeklyDatePicker';
import { DownArrow, ToastClose, ToastError, ToastSuccess } from '../components/helpers/svgHelper';

function Driver({
  fleets,
  week,
  setWeek,
  selectStyles,
  midMultiSelectStyles,
  fetchFleetPayments,
  loading,
  setLoading,
  entitySelectedFleets,
  onEntityFleetChange,
  handleSaveClick,
  handleUpdateAll,
  downloadPdf,
  handleUpload,
  downloadTemplate,
  selectedWeek,
  isModalOpen,
  setIsModalOpen,
  modalLoading,
  isStatementsModalOpen,
  setStatementsModalOpen,
  dropdown,
  setDropdown,
  driverTable,
  fleetData,
  paymentBreakdown,
  tripBreakdown,
  incentiveBreakdown,
  fleetBalance,
  rideshareBreakdown,
  setSelectedFile,
  selectedRows,
  setSelectedRows,
  itemLoad,
  editMode,
  setEditMode,
  editedValues,
  setEditedValues,
  toast,
  resetDriverState,
  downloadDriverCsv,
  uploadErrors,
  clearUploadErrors,
})
  {

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  // Fleet methods

  useEffect(() => {
    resetDriverState();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (loading) {
        try {
          await fetchFleetPayments();
        } catch (error) {
          console.error("Error fetching fleet payments:", error);
        } finally {
          setLoading(false);
        }
      }
    };
  
    loadData();
  }, [loading]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openStatementsModal = () => {
    setStatementsModalOpen(true);
  };

  const closeStatementsModal = () => {
    setStatementsModalOpen(false);
  };

  const handleEditClick = (accountId, item) => {
    setEditMode((prev) => ({ ...prev, [accountId]: true }));
    setEditedValues((prev) => ({
      ...prev,
      [accountId]: {
        bonus: item.snapshot.bonus,
        performance_expense: item.snapshot.performance_expense,
        fuel: item.snapshot.fuel,
        km_overuse: item.snapshot.km_overuse,
        deduction: item.snapshot.deduction,
        driver_balance_security: item.snapshot.driver_balance_security,
        access_to_site: item.snapshot.access_to_site,
        other: item.snapshot.other,
        uber: item.snapshot.uber,
        bolt: item.snapshot.bolt,
      },
    }));
  };

  const handleChange = (accountId, field, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [field]: value,
      },
    }));
  };

  const handleCancelEdit = (accountId) => {
    setEditMode((prev) => {
      const updated = { ...prev };
      delete updated[accountId];
      return updated;
    });
  
    setEditedValues((prev) => {
      const updated = { ...prev };
      delete updated[accountId];
      return updated;
    });
  };

  return (
    <>
      {loading && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-700 opacity-50 flex flex-col items-center justify-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 h-12 w-12 mb-4"></div>
        </div>
      )}

      {toast.show && (
        <div className={`${toast.type === "success" ? "bg-green-500" : "bg-red-400"} fixed top-50 right-50 left-50 w-[400px] p-4 shadow-lg rounded-lg flex items-center justify-between border border-gray-300 z-50`}>
          <div className="flex items-center gap-3">
            {toast.type === "success" ? (
              <ToastSuccess />
            ) : (
              <ToastError />
            )}
            <div>
              <p className="text-white font-medium">{toast.message}</p>
            </div>
          </div>
          <button onClick={() => setToast({ show: false })}>
           <ToastClose />
          </button>
        </div>
      )}
      <div className="bg-transparent pt-2 pb-4 space-y-4">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row w-9/12 space-x-2 mb-4" style={{ height: '50px'}}>
            <div className="w-2/5 relative">
              <EntityMultiSelect
                availableItems={fleets}
                selectedItems={entitySelectedFleets}
                setSelectedItems={onEntityFleetChange}
                primaryField="name"
                searchFields={["name"]}
                entityLabel="Fleet"
                entityLabelPlural="Fleets"
                compact
                showChevron
              />
            </div>

            <div className="relative inline-block text-left">
              <div>
                <button
                  type="button"
                  style={{ width: '150px' }}
                  onClick={() => setDropdown(!dropdown)}
                  className="inline-flex w-full justify-between items-center gap-x-1.5 border-gray-400 border rounded-md bg-white text-gray-400 px-3 py-3 text-xxs font-thin shadow-sm">
                  {week.from} - {week.to}
                  <DownArrow />
                </button>
              </div>
              {dropdown && <WeeklyDatePicker setWeek={setWeek} week={week} dropdown={dropdown} setDropdown={setDropdown} />}
            </div>

            <div className="flex space-x-2" style={{ height: '45px'}}>
              <button
                disabled={entitySelectedFleets.length === 0}
                onClick={() => setLoading(true)}
                className={entitySelectedFleets.length === 0 ? "bg-gray-400 text-gray-600 font-normal text-sm py-3 px-4 rounded-lg" : "bg-purple-600 text-white font-normal text-sm py-3 px-4 rounded-lg"}>
                Apply Filters
              </button>

              <button
                onClick={resetDriverState}
                className="border border-gray-300 text-gray-600 font-normal text-sm py-3 px-4 rounded-lg">
                Reset Filters
              </button>
            </div>
          </div>
          <div className="flex space-x-2" style={{ height: '45px'}}>
            <button
              onClick={() => downloadDriverCsv?.(week)}
              disabled={fleetData === null}
              className={fleetData === null ? "bg-gray-400 text-gray-600 font-normal text-sm py-3 px-4 rounded-lg cursor-not-allowed" : "bg-purple-600 text-white font-normal text-sm py-3 px-4 rounded-lg"}>
              Download CSV
            </button>
            <button
              onClick={openModal}
              disabled={fleetData === null}
              className={fleetData === null ? "bg-gray-400 text-gray-600 font-normal text-sm py-3 px-4 rounded-lg cursor-not-allowed" : "bg-purple-600 text-white font-normal text-sm py-3 px-4 rounded-lg"}>
              Upload Bonuses & Earnings
            </button>
            <button
              onClick={openStatementsModal}
              disabled={fleetData === null}
              className={fleetData === null ? "bg-gray-400 text-gray-600 font-normal text-sm py-3 px-4 rounded-lg cursor-not-allowed" : "bg-purple-600 text-white font-normal text-sm py-3 px-4 rounded-lg"}>
              Statements
            </button>
          </div>
        </div>

        {entitySelectedFleets.length !== 0 && fleetData !== null && (
          <>
            <div className="flex flex-row space-x-2">
              <div className="w-7/12">
                <Summary title="Driver Breakdown" breakdown={fleetData} />
              </div>
              <div className="w-4/12">
                <Summary
                  title="Ride Share Breakdown"
                  breakdown={rideshareBreakdown}
                />
              </div>
            </div>

            <div className="flex flex-row space-x-2">
              <div className="w-3/12">
                <Summary
                  title="Payment Breakdown"
                  breakdown={paymentBreakdown}
                />
              </div>
              <div className="w-2/12">
                <Summary title="Trip Breakdown" breakdown={tripBreakdown} />
              </div>
              <div className="w-4/12">
                <Summary
                  title="Incentive Breakdown"
                  breakdown={incentiveBreakdown}
                />
              </div>
              <div className="w-2/12">
                <Summary
                  title="Fleet Balance"
                  breakdown={fleetBalance}
                />
              </div>
            </div>

            <div
              className="flex flex-row p-1 rounded-lg"
              style={{ background: '#EEEDF0' }}>
            </div>
            <div
              className="overflow-y-auto px-2 scrollbar"
              style={{ maxHeight: '800px' }}
            >
              <SampleTable
                data={driverTable}
                week={week}
                handleEditClick={handleEditClick}
                handleChange={handleChange}
                handleCancelEdit={handleCancelEdit}
                handleUpdateAll={handleUpdateAll}
                handleSaveClick={handleSaveClick}
                editMode={editMode}
                editedValues={editedValues}
                itemLoad={itemLoad}
              />
            </div>

            {isModalOpen && entitySelectedFleets.length !== 0 && (
              <BonusesModal
                closeModal={closeModal}
                options={driverTable}
                selectedWeek={selectedWeek}
                multiSelectStyles={midMultiSelectStyles}
                selectStyles={selectStyles}
                sampleData={driverTable}
                downloadTemplate={downloadTemplate}
                handleFileSelect={handleFileSelect}
                handleUpload={handleUpload}
                modalLoading={modalLoading}
                toast={toast}
                uploadErrors={uploadErrors}
                clearUploadErrors={clearUploadErrors}
              />
            )}
            {isStatementsModalOpen && (
              <StatementsModal
                closeModal={closeStatementsModal}
                options={driverTable}
                fleets={fleets}
                selectedWeek={week.from}
                multiSelectStyles={midMultiSelectStyles}
                selectStyles={selectStyles}
                data={driverTable}
                handleGeneratePdf={downloadPdf}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                // loading={loading}
                modalLoading={modalLoading}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Driver;
